import React, { useEffect, useRef, useState } from "react";
import { aspectOf, aspectRatios, type AspectKey } from "@veyso/core";

/**
 * What shape the finished video is.
 *
 * In the header rather than in a panel, because it is a fact about the whole
 * project — like its length — and not a property of whatever happens to be
 * selected. It sits beside Steps for the same reason: those are the two
 * controls that describe the project rather than edit it.
 *
 * Changing it reflows everything. Card elements are stored as fractions of the
 * frame, so a title spanning 84% of a wide frame still spans 84% of a tall
 * one — which is correct arithmetic and a different-looking card. The
 * recording itself is safe: `cardBox` fits it at its own aspect inside
 * whatever frame it is given, so a 16:9 capture in a 9:16 video is letterboxed
 * rather than stretched.
 */
const GLYPHS: Record<AspectKey, { w: number; h: number }> = {
  "16:9": { w: 18, h: 10 },
  "9:16": { w: 10, h: 18 },
  "4:5": { w: 13, h: 16 },
  "1:1": { w: 15, h: 15 },
};

/** The shape itself, drawn. Four numbers are harder to tell apart than four rectangles. */
const Glyph: React.FC<{ which: AspectKey }> = ({ which }) => {
  const { w, h } = GLYPHS[which];
  return (
    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" aria-hidden="true">
      <span className="rounded-[3px] border-[1.5px] border-current" style={{ width: w, height: h }} />
    </span>
  );
};

export const AspectPicker: React.FC<{
  timeline: { width: number; height: number };
  onChange: (size: { width: number; height: number }) => void;
  /** Shown but not offered, as while a language is viewed: the shape is the English's. */
  disabled?: boolean;
}> = ({ timeline, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const current = aspectOf(timeline);

  useEffect(() => {
    if (!open) return;
    /** A click anywhere else closes it, which is what a menu does. */
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        title="The shape of the finished video"
      >
        {/* A shape that is neither is shown as its numbers rather than as one
            of the four lit up. A project can carry any size the schema allows,
            and claiming it is 1:1 because that is nearest would be a lie the
            menu then acts on. */}
        {current ? <Glyph which={current} /> : null}
        {current ?? `${timeline.width}×${timeline.height}`}
      </button>

      {open && !disabled ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-[248px] overflow-hidden rounded-[10px] border border-[var(--edge)] bg-bay-900 py-1 shadow-lg"
        >
          {aspectRatios.map((option) => {
            const active = option.key === current;
            return (
              <button
                key={option.key}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-ink-100 transition-colors hover:bg-bay-800"
                onClick={() => {
                  setOpen(false);
                  if (!active) onChange({ width: option.width, height: option.height });
                }}
              >
                <Glyph which={option.key} />
                <span className="font-semibold">{option.key}</span>
                <span className="truncate text-[12px] text-ink-300">{option.use}</span>
                {active ? <span className="ml-auto text-ember-600">✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
